<script setup>
// Fase 59 — Pipeline › Oportunidades: o catálogo do que a conta vende.
//
// É o que alimenta o registro de uma venda: com produtos cadastrados, marcar um
// card como ganho passa a ser escolher o que foi vendido em vez de digitar um
// valor solto. Sem catálogo, o fluxo antigo continua funcionando — por isso
// esta tela tem estado vazio explicativo, não bloqueio.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PipelineProductsAPI from 'dashboard/api/pipeline/products';
import PipelinesAPI from 'dashboard/api/pipelines';
import ProductFormModal from './ProductFormModal.vue';
import {
  formatLocalizedCurrency,
  formatLocalizedDateInTimezone,
  normalizeLocale,
  resolveAccountCurrency,
} from '../utils/localeFormatting';
import { normalizeText } from '../helpers/columnFilterMatchers';
import { reportingDatePreset } from '../helpers/reportingDatePresets';

const { t, locale } = useI18n();
const store = useStore();
const { showAlert } = useAlert();
const { accountId, currentAccount } = useAccount();
const currentAccountId = accountId;
const { checkPermissions } = usePolicy();

// Ler o catálogo é liberado a quem enxerga o Pipeline (o modal de ganho precisa
// listar o que pode ser vendido), mas configurar é ato de gestão — mesmo
// recorte de `PipelineProductPolicy#account_user_can_manage?`. Sem isto a tela
// oferecia Novo/Editar/Desativar a quem só lê, e todo clique voltava 403.
const canManage = computed(() =>
  checkPermissions(['administrator', 'pipeline_manage'])
);

const products = ref([]);
const pipelines = ref([]);
const loading = ref(false);
// ⚠️ Guarda um SINALIZADOR, não a string traduzida. `t()` chamado dentro do
// catch congela o texto no idioma vigente naquele instante — e o locale da
// conta só é aplicado depois do boot, então um erro no primeiro carregamento
// ficava em inglês para sempre num app em pt-BR. Traduzir no template mantém a
// mensagem reativa ao idioma.
const loadFailed = ref(false);
const showForm = ref(false);
const editing = ref(null);

const accountCurrency = computed(() => resolveAccountCurrency(store));

// A moeda é do PRODUTO, não da conta: o catálogo aceita produto em moeda
// diferente, e formatar tudo em BRL exibiria um produto em USD como se fosse
// real — mesmo número, significado outro.
const formatMoney = (value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const code = currency || accountCurrency.value;
  try {
    return formatLocalizedCurrency(number, {
      currency: code,
      locale: locale.value,
    });
  } catch {
    return `${code} ${number}`;
  }
};

const pipelineNames = product => {
  // Restrição corrompida chega como lista vazia + sinalizador. Ler isso como
  // "todos os funis" seria o contrário da verdade: o servidor trata o produto
  // como indisponível em TODO lugar (fail-closed).
  if (product.pipeline_ids_malformed)
    return t('PIPELINE.PRODUCTS.RESTRICTION_BROKEN');
  if (!product.pipeline_ids?.length)
    return t('PIPELINE.PRODUCTS.ALL_PIPELINES');
  return product.pipeline_ids
    .map(
      id =>
        pipelines.value.find(p => p.id === id)?.name ||
        t('PIPELINE.PRODUCTS.REMOVED_PIPELINE', { id })
    )
    .join(', ');
};

// Teto do `per_page` do backend. O catálogo não tem limite de tamanho, então
// uma única página deixaria o produto 201 invisível — e invisível aqui é
// ineditável, já que esta é a única tela do catálogo.
const PAGE_SIZE = 200;

const fetchPipelines = async () => {
  // `get` exige o accountId: sem ele lança TypeError ANTES de sair a
  // requisição, e o Promise.all inteiro caía no catch — a tela mostrava
  // "não foi possível carregar" mesmo com o catálogo respondendo 200.
  const response = await PipelinesAPI.get(accountId.value);
  const payload = response.data?.payload || response.data || [];
  return Array.isArray(payload) ? payload : [];
};

const fetchProducts = async isCurrent => {
  const collected = [];
  let offset = 0;
  let total = null;

  do {
    // Trocou de conta no meio da paginação? Para de buscar. Antes o resultado
    // era descartado, mas as páginas seguintes continuavam saindo — requisições
    // da conta anterior contra o servidor da conta nova.
    if (!isCurrent()) return collected;
    // eslint-disable-next-line no-await-in-loop
    const { data } = await PipelineProductsAPI.list({
      perPage: PAGE_SIZE,
      offset,
    });
    // Um 200 com shape inesperado NÃO pode virar "catálogo vazio": o operador
    // cadastraria de novo o que já existe. Payload inválido é falha de carga.
    if (!Array.isArray(data?.products)) throw new Error('payload inválido');
    const page = data.products;
    collected.push(...page);
    total = Number(data?.meta?.total_count);
    offset += PAGE_SIZE;
    // Página vazia encerra: sem isso um total_count inconsistente viraria
    // laço infinito.
    if (!page.length) break;
  } while (Number.isFinite(total) && collected.length < total);

  return collected;
};

// Cada carga invalida a anterior: uma resposta em voo da conta antiga não pode
// pintar a tela da conta nova.
// Ponteiro preenchido mais abaixo: `load` precisa disparar o desempenho, mas
// `loadPerformance` depende de coisas declaradas depois dele.
let reloadPerformance = () => {};
// Mesmo motivo: o watch de conta roda com `immediate` durante o setup, antes de
// os refs de filtro existirem — referenciá-los direto seria erro de TDZ.
let resetFilters = () => {};

let requestId = 0;

const load = async () => {
  requestId += 1;
  const currentRequest = requestId;
  loading.value = true;
  loadFailed.value = false;
  try {
    const [loadedProducts, loadedPipelines] = await Promise.all([
      fetchProducts(() => currentRequest === requestId),
      fetchPipelines(),
    ]);
    if (currentRequest !== requestId) return;
    products.value = loadedProducts;
    pipelines.value = loadedPipelines;
    // Chamada tardia de propósito: `load` roda no watch de conta, e o
    // desempenho é carregado depois que o catálogo já está na tela — a lista
    // não espera pelos números.
    reloadPerformance();
  } catch {
    if (currentRequest === requestId) loadFailed.value = true;
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
};

// Recarrega ao TROCAR DE CONTA. O Vue Router reaproveita o componente quando só
// o `:accountId` muda: lendo o id apenas na montagem, o catálogo, os preços e
// os funis da conta anterior continuariam na tela da conta nova.
watch(
  accountId,
  () => {
    products.value = [];
    pipelines.value = [];
    // Os filtros são da conta ANTERIOR. O de funil chega a ir para o servidor:
    // um id de outro inquilino não casa com nada e a tela anunciava zero como
    // se fosse medição.
    resetFilters();
    // Fecha o formulário também: aberto, ele continuaria exibindo nome, preço e
    // restrições do produto da conta ANTERIOR — e um cadastro pela metade
    // poderia ser salvo dentro da conta nova.
    showForm.value = false;
    editing.value = null;
    load();
  },
  { immediate: true }
);

const openNew = () => {
  editing.value = null;
  showForm.value = true;
};

const openEdit = product => {
  editing.value = product;
  showForm.value = true;
};

const onSaved = () => {
  showAlert(t('PIPELINE.PRODUCTS.SAVED'));
  load();
};

const deactivate = async product => {
  try {
    await PipelineProductsAPI.deactivate(product.id);
    showAlert(t('PIPELINE.PRODUCTS.DEACTIVATED'));
    load();
  } catch {
    showAlert(t('PIPELINE.PRODUCTS.SAVE_ERROR'), 'error');
  }
};

const reactivate = async product => {
  try {
    await PipelineProductsAPI.reactivate(product.id);
    showAlert(t('PIPELINE.PRODUCTS.REACTIVATED'));
    load();
  } catch {
    showAlert(t('PIPELINE.PRODUCTS.SAVE_ERROR'), 'error');
  }
};

// ---------------------------------------------------------------------------
// Busca, facetas e ordenação — tudo no cliente.
//
// O catálogo inteiro já vem carregado (a paginação acima varre todas as
// páginas), então filtrar aqui é instantâneo e não gasta uma ida ao servidor a
// cada tecla. Se o catálogo crescer a ponto de doer, isto vira parâmetro de
// API — mas o contrato da tela não muda.
// ---------------------------------------------------------------------------
const search = ref('');
const statusFilter = ref('all');
const pipelineFilter = ref(null);
const categoryFilter = ref(null);
const sortBy = ref('name');

const matchesSearch = product => {
  const term = normalizeText(search.value).trim();
  if (!term) return true;
  return [product.name, product.sku, product.category].some(field =>
    normalizeText(field).includes(term)
  );
};

const matchesStatus = product => {
  if (statusFilter.value === 'active') return product.active;
  if (statusFilter.value === 'inactive') return !product.active;
  return true;
};

// Predicado ÚNICO de "este produto pertence a este funil", usado tanto pela
// lista quanto pela contagem da faceta — duas respostas diferentes para a mesma
// pergunta na mesma tela é pior que não ter faceta.
const matchesPipelineId = (product, pipelineId) => {
  // Restrição corrompida é fail-closed no servidor: não pertence a funil
  // nenhum, nem à faceta "vale em todos".
  if (product.pipeline_ids_malformed) return false;
  const ids = product.pipeline_ids || [];
  // A disponibilidade é de HOJE; a medição é HISTÓRICA. Um produto vendido no
  // funil A e depois restrito ao B some da lista, mas a receita dele em A
  // continua no período — e o resumo, que soma o que a tela mostra, perderia
  // esse dinheiro sem dizer nada.
  //
  // Só vale quando a MEDIÇÃO está recortada por ESTE funil: sem recorte a
  // resposta é da conta inteira, e qualquer produto vendido em qualquer lugar
  // passaria a "pertencer" a todos os funis, inflando as facetas.
  const medidoNesteFunil = measurablePipelineId.value === pipelineId;
  if (medidoNesteFunil && performanceOf(product).length) return true;
  // Produto irrestrito aparece TAMBÉM ao filtrar por um funil específico: ele
  // realmente está disponível ali. Escondê-lo daria a resposta errada para
  // "o que posso vender neste funil?".
  return ids.length === 0 || ids.includes(pipelineId);
};

const matchesPipeline = product => {
  if (pipelineFilter.value === null) return true;
  if (product.pipeline_ids_malformed) return false;
  if (pipelineFilter.value === 'unrestricted') {
    return (product.pipeline_ids || []).length === 0;
  }
  return matchesPipelineId(product, pipelineFilter.value);
};

// Compara APARADO dos dois lados: a faceta é construída com `trim()`, então
// uma categoria gravada como " Serviço " gerava faceta com contagem positiva
// que, ao ser clicada, não casava com nada.
const matchesCategory = product =>
  categoryFilter.value === null ||
  product.category?.trim() === categoryFilter.value;

// `null`, `undefined` e string vazia são "sem preço"; só um número finito é
// preço de verdade. `Number(null) === 0` é a armadilha aqui.
const priceOf = product => {
  const raw = product.default_value;
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const filteredProducts = computed(() => {
  const result = products.value.filter(
    product =>
      matchesSearch(product) &&
      matchesStatus(product) &&
      matchesPipeline(product) &&
      matchesCategory(product)
  );

  // `normalizeLocale`: o locale do app é `pt_BR` (underscore), que NÃO é uma
  // tag BCP 47 — `localeCompare` levanta RangeError e derruba a tela inteira.
  // O helper do módulo existe exatamente para isso, e é o mesmo que a
  // formatação de moeda já usa.
  const byName = (a, b) =>
    String(a.name ?? '').localeCompare(
      String(b.name ?? ''),
      normalizeLocale(locale.value)
    );

  if (sortBy.value === 'value_desc') {
    return [...result].sort((a, b) => {
      const av = priceOf(a);
      const bv = priceOf(b);
      // SEM PREÇO vai para o fim de TUDO, antes de qualquer agrupamento: não
      // pertence a grupo de valor nenhum. `Number(null)` e `Number('')` são
      // zero, não NaN — sem o teste explícito, "sem preço" e "custa zero"
      // ficariam indistinguíveis.
      if (av === null || bv === null) {
        if (av === null && bv === null) return byName(a, b);
        return av === null ? 1 : -1;
      }
      // Moeda depois: 1.000 BRL não é "maior" que 900 USD — são grandezas
      // diferentes, e ordená-las juntas produz um ranking que mente. A moeda da
      // conta vem primeiro; o resto agrupa por código.
      const ca = a.currency || accountCurrency.value;
      const cb = b.currency || accountCurrency.value;
      if (ca !== cb) {
        if (ca === accountCurrency.value) return -1;
        if (cb === accountCurrency.value) return 1;
        return ca.localeCompare(cb);
      }
      return bv - av || byName(a, b);
    });
  }
  if (sortBy.value === 'recent') {
    return [...result].sort(
      (a, b) =>
        new Date(b.created_at ?? 0) - new Date(a.created_at ?? 0) ||
        byName(a, b)
    );
  }
  return [...result].sort(byName);
});

// Contagem das facetas sobre o catálogo INTEIRO, não sobre o resultado: uma
// faceta que só conta o que já está filtrado mostra zero em tudo que o usuário
// ainda não escolheu, e ele deixa de descobrir o que existe.
const statusCounts = computed(() => ({
  all: products.value.length,
  active: products.value.filter(product => product.active).length,
  inactive: products.value.filter(product => !product.active).length,
}));

const isUnrestricted = product =>
  !product.pipeline_ids_malformed && !product.pipeline_ids?.length;

const pipelineFacets = computed(() => {
  const unrestricted = products.value.filter(isUnrestricted).length;
  const named = pipelines.value
    .map(pipeline => ({
      id: pipeline.id,
      name: pipeline.name,
      // Conta com o MESMO predicado que monta a lista — inclusive o produto que
      // vendeu ali e hoje está restrito a outro funil. Uma faceta dizendo 1 com
      // a lista trazendo 2 é pior que faceta nenhuma.
      count: products.value.filter(product =>
        matchesPipelineId(product, pipeline.id)
      ).length,
    }));
  // Nenhum filtro aqui: a faceta deixou de ser só filtro de lista — escolher um
  // funil é o único jeito de recortar a MEDIÇÃO. Esconder a que "não estreita a
  // lista" tirava a função inteira, e um `count > 0` sumia com todas justamente
  // na conta de catálogo vazio, que é onde esta tela existe para explicar a
  // receita histórica sem produto vinculado.
  return { unrestricted, named };
});

const categoryFacets = computed(() => {
  const counts = new Map();
  products.value.forEach(product => {
    const category = product.category?.trim();
    if (!category) return;
    counts.set(category, (counts.get(category) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) =>
      a.name.localeCompare(b.name, normalizeLocale(locale.value))
    );
});

const hasActiveFilters = computed(
  () =>
    search.value.trim() !== '' ||
    statusFilter.value !== 'all' ||
    pipelineFilter.value !== null ||
    categoryFilter.value !== null
);

const clearFilters = () => {
  search.value = '';
  statusFilter.value = 'all';
  pipelineFilter.value = null;
  categoryFilter.value = null;
};

resetFilters = clearFilters;

// ---------------------------------------------------------------------------
// Medição por período.
//
// A data filtra a MEDIÇÃO, não a lista: mudar o período muda quanto cada
// produto vendeu, não quais produtos existem. Produto sem venda no período
// continua listado com "—" — é justamente ele o dado mais acionável.
// ---------------------------------------------------------------------------
const period = ref('30');
const customStart = ref('');
const customEnd = ref('');
const performanceByProduct = ref({});
const unlinkedSales = ref([]);
const performanceZone = ref('');
const performanceFailed = ref(false);
const performanceLoading = ref(false);

// O período é calculado no fuso DA CONTA, não no do navegador: um usuário em
// Lisboa e outro em São Paulo têm de medir o mesmo "últimos 30 dias", e é esse
// o fuso em que o servidor resolve o intervalo. Reusa o helper que o filtro do
// board já usa.
const reportingTimezone = computed(
  () =>
    currentAccount.value?.reporting_timezone ||
    currentAccount.value?.settings?.reporting_timezone ||
    'UTC'
);

const PERIOD_PRESETS = {
  7: 'last_7',
  30: 'last_30',
  90: 'last_90',
  month: 'this_month',
};

// Intervalo em DATAS (o servidor resolve no fuso da conta e estende o último
// dia até o fim). Mandar timestamp daqui reintroduziria o fuso do navegador.
const periodRange = computed(() => {
  if (period.value === 'all') return { start: null, end: null };
  if (period.value === 'custom') {
    return { start: customStart.value || null, end: customEnd.value || null };
  }
  return reportingDatePreset(PERIOD_PRESETS[period.value], {
    reportingTimezone: reportingTimezone.value,
  });
});

// Intervalo invertido é erro do usuário, não do servidor: avisar aqui evita uma
// ida ao backend só para receber 422 — e o backend continua recusando.
const invertedRange = computed(() => {
  const { start, end } = periodRange.value;
  return Boolean(start && end && start > end);
});

// "Personalizado" sem nenhuma data ainda não é um período: medir aqui dispararia
// a consulta mais cara que existe (o histórico inteiro) e exibiria o resultado
// sem régua nenhuma, como se fosse o recorte pedido.
const incompleteCustomRange = computed(
  () => period.value === 'custom' && !customStart.value && !customEnd.value
);

const unusableRange = computed(
  () => invertedRange.value || incompleteCustomRange.value
);

// A faceta de funil tem um valor que NÃO é funil: 'unrestricted' seleciona os
// produtos sem restrição. Ele estreita a lista, mas não é um recorte que o
// servidor saiba medir — a medição continua a do escopo inteiro.
const measurablePipelineId = computed(() =>
  Number.isInteger(pipelineFilter.value) ? pipelineFilter.value : undefined
);

// Sem medição válida na tela: ou está medindo, ou o intervalo pedido não é
// medível. Nos dois casos o número exibido seria de OUTRO período — a mesma
// mentira, por dois caminhos.
const measurementPending = computed(
  () => performanceLoading.value || unusableRange.value
);

// Medição CONHECIDA: nem pendente, nem falha. Falha não é zero — a receita do
// período continua existindo, só não foi possível lê-la. Exibir "0" aqui
// transformaria um resultado desconhecido num zero de aparência legítima.
const measurementKnown = computed(
  () => !measurementPending.value && !performanceFailed.value
);

let performanceRequestId = 0;

const loadPerformance = async () => {
  // Invalida o pedido em voo ANTES de desistir: sem incrementar, uma resposta
  // já a caminho ainda passaria pelo guard e publicaria números sob um
  // intervalo que a tela está recusando.
  performanceRequestId += 1;
  const atual = performanceRequestId;
  if (unusableRange.value) {
    performanceByProduct.value = {};
    unlinkedSales.value = [];
    // Limpa o erro DA CONSULTA ANTERIOR: mantê-lo aqui culparia a rede por um
    // período que a tela nem chegou a pedir.
    performanceFailed.value = false;
    performanceZone.value = '';
    performanceLoading.value = false;
    return;
  }

  // A conta em que o pedido NASCEU. Sem isto, trocar de conta com um pedido em
  // voo pinta a receita da conta anterior sob o nome da nova.
  const contaDoPedido = currentAccountId.value;
  performanceFailed.value = false;
  performanceZone.value = '';
  // Zerar ANTES: número velho sob rótulo de período novo é pior que célula
  // vazia — parece medição, e não é.
  performanceByProduct.value = {};
  unlinkedSales.value = [];
  performanceLoading.value = true;
  try {
    const { start, end } = periodRange.value;
    const { data } = await PipelineProductsAPI.performance({
      wonStart: start,
      wonEnd: end,
      // Sem isto, filtrar por funil estreitava a LISTA e deixava os números
      // medindo a conta inteira: a tela parecia recortada e não estava.
      //
      // Só id NUMÉRICO: a faceta "irrestritos" é o sentinela 'unrestricted',
      // que o servidor recusa com 422 — e a tela zerava todos os números por
      // causa de um filtro que ela mesma ofereceu.
      pipelineId: measurablePipelineId.value,
    });
    if (
      atual !== performanceRequestId ||
      contaDoPedido !== currentAccountId.value
    )
      return;
    performanceByProduct.value = (data?.products || []).reduce((acc, item) => {
      acc[item.id] = item.performance || [];
      return acc;
    }, {});
    unlinkedSales.value = data?.unlinked || [];
    performanceZone.value = data?.meta?.time_zone || '';
  } catch {
    if (
      atual !== performanceRequestId ||
      contaDoPedido !== currentAccountId.value
    )
      return;
    // Falha aqui NÃO derruba o catálogo: a lista continua utilizável e o aviso
    // diz que só os números estão faltando.
    performanceFailed.value = true;
    performanceByProduct.value = {};
    unlinkedSales.value = [];
  } finally {
    if (atual === performanceRequestId) performanceLoading.value = false;
  }
};

reloadPerformance = loadPerformance;

watch(period, loadPerformance);
watch([customStart, customEnd], () => {
  if (period.value === 'custom') loadPerformance();
});
// O fuso da conta ENTRA no cálculo do intervalo, e ele chega tarde: no primeiro
// render `currentAccount` ainda não hidratou e o preset é calculado em UTC.
// Sem reagir aqui, o rótulo mudava de dia quando a conta carregava e os números
// continuavam sendo os da janela anterior — receita sob o intervalo errado.
watch(reportingTimezone, loadPerformance);
// O funil escolhido faz parte do RECORTE medido, não só do que a lista mostra.
watch(measurablePipelineId, loadPerformance);

// Uma linha de desempenho por MOEDA: somar BRL com USD produziria um número
// que não existe.
const performanceOf = product => performanceByProduct.value[product.id] || [];

const salesCountOf = product =>
  performanceOf(product).reduce(
    (total, row) => total + Number(row.sales_count || 0),
    0
  );

// A faixa mede o PERÍODO, não o catálogo: soma o que tem produto vinculado e o
// que não tem. Contando só o vinculado, a tela anunciava "0 vendas" tendo 14
// logo abaixo na faixa âmbar — dois números contraditórios na mesma tela, e o
// menor deles em corpo maior.
const totals = computed(() => {
  const porMoeda = new Map();
  let vendas = 0;
  let produtosComVenda = 0;
  // Acumula em CENTAVOS inteiros: somar decimais em ponto flutuante faz o total
  // da tela virar 1234.5600000000001 depois de algumas parcelas.
  const acumular = linha => {
    vendas += Number(linha.sales_count || 0);
    const centavos = Math.round(Number(linha.total_value || 0) * 100);
    porMoeda.set(linha.currency, (porMoeda.get(linha.currency) || 0) + centavos);
  };
  // Soma o que a tela MOSTRA. Percorrendo o catálogo inteiro, filtrar por funil
  // (ou buscar) estreitava a lista e deixava o resumo somando produtos que já
  // não estão nela — dois recortes na mesma tela.
  filteredProducts.value.forEach(product => {
    const linhas = performanceByProduct.value[product.id] || [];
    if (linhas.length) produtosComVenda += 1;
    linhas.forEach(acumular);
  });
  unlinkedSales.value.forEach(acumular);
  return {
    porMoeda: [...porMoeda.entries()].map(([currency, centavos]) => ({
      currency,
      value: centavos / 100,
    })),
    vendas,
    produtosComVenda,
  };
});

const periodLabel = computed(() => {
  const zone = performanceZone.value || reportingTimezone.value;
  if (period.value === 'all') {
    return t('PIPELINE.PRODUCTS.PERIOD_ALL_RESOLVED', { zone });
  }
  const { start, end } = periodRange.value;
  if (!start && !end) return '';
  // `start`/`end` JÁ são datas de calendário NO FUSO DA CONTA — não instantes.
  // Passá-las por um formatador de instante faz "2026-07-01" ser lido como
  // meia-noite UTC e, num fuso a oeste, exibido como 30/06: o rótulo mostraria
  // um dia a menos que o período realmente medido.
  const formatar = iso => {
    if (!iso) return '…';
    const [ano, mes, dia] = iso.split('-').map(Number);
    return formatLocalizedDateInTimezone(new Date(ano, mes - 1, dia), {
      locale: normalizeLocale(locale.value),
      dateStyle: 'medium',
    });
  };
  return t('PIPELINE.PRODUCTS.PERIOD_RESOLVED', {
    range: `${formatar(start)} – ${formatar(end)}`,
    zone,
  });
});

// Catálogo vazio e busca sem resultado são estados DIFERENTES: o primeiro
// convida a cadastrar, o segundo a afrouxar o filtro. Um texto só para os dois
// manda o usuário para o lado errado.
const isEmpty = computed(
  () => !loading.value && !loadFailed.value && products.value.length === 0
);
const hasNoResults = computed(
  () =>
    !loading.value &&
    !loadFailed.value &&
    products.value.length > 0 &&
    filteredProducts.value.length === 0
);
</script>

<template>
  <!-- w-full/flex-1: o container pai é um flex ROW; sem isso a página
       encolhe até a largura do conteúdo e sobra metade da tela em branco. -->
  <div class="flex h-full w-full flex-1 flex-col overflow-hidden bg-n-solid-1">
    <header
      class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-6 py-4"
    >
      <div>
        <h1 class="text-heading-1 font-semibold text-n-slate-12">
          {{ t('PIPELINE.PRODUCTS.PAGE_TITLE') }}
        </h1>
        <p class="mt-1 text-body-main text-n-slate-11">
          {{ t('PIPELINE.PRODUCTS.PAGE_SUBTITLE') }}
        </p>
      </div>
      <Button
        v-if="canManage"
        color="blue"
        size="md"
        :label="t('PIPELINE.PRODUCTS.NEW')"
        @click="openNew"
      />
    </header>

    <div class="flex-1 overflow-y-auto px-6 py-5">
      <p
        v-if="loading"
        class="py-10 text-center text-body-main text-n-slate-11"
      >
        {{ t('PIPELINE.PRODUCTS.LOADING') }}
      </p>

      <p
        v-else-if="loadFailed"
        class="rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
        role="alert"
      >
        {{ t('PIPELINE.PRODUCTS.LOAD_ERROR') }}
      </p>

      <!-- Medição: vale com catálogo cheio OU vazio. A conta pode já ter
           vendido muito antes de existir catálogo — escondendo a régua aqui,
           quem tem R$ 200 mil registrados vê só "cadastre seu primeiro
           produto", sem poder sequer trocar o período. -->
      <template v-else>
        <div
          class="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2.5"
        >
          <div class="flex flex-wrap gap-1 rounded-lg bg-n-alpha-1 p-1">
            <button
              v-for="option in [
                { key: '7', label: t('PIPELINE.PRODUCTS.PERIOD_7') },
                { key: '30', label: t('PIPELINE.PRODUCTS.PERIOD_30') },
                { key: '90', label: t('PIPELINE.PRODUCTS.PERIOD_90') },
                { key: 'month', label: t('PIPELINE.PRODUCTS.PERIOD_MONTH') },
                { key: 'custom', label: t('PIPELINE.PRODUCTS.PERIOD_CUSTOM') },
                { key: 'all', label: t('PIPELINE.PRODUCTS.PERIOD_ALL') },
              ]"
              :key="option.key"
              type="button"
              class="rounded-md px-2.5 py-1 text-label-small font-medium transition-colors"
              :class="
                period === option.key
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              :aria-pressed="period === option.key"
              @click="period = option.key"
            >
              {{ option.label }}
            </button>
          </div>

          <div v-if="period === 'custom'" class="flex items-center gap-2">
            <label class="flex items-center gap-1.5">
              <span class="text-label-small text-n-slate-10">
                {{ t('PIPELINE.PRODUCTS.PERIOD_FROM') }}
              </span>
              <input
                v-model="customStart"
                type="date"
                name="won-start"
                class="h-8 rounded-lg border border-n-weak bg-n-solid-1 !mb-0 px-2 text-sm text-n-slate-12"
              />
            </label>
            <label class="flex items-center gap-1.5">
              <span class="text-label-small text-n-slate-10">
                {{ t('PIPELINE.PRODUCTS.PERIOD_TO') }}
              </span>
              <input
                v-model="customEnd"
                type="date"
                name="won-end"
                class="h-8 rounded-lg border border-n-weak bg-n-solid-1 !mb-0 px-2 text-sm text-n-slate-12"
              />
            </label>
          </div>

          <!-- O intervalo resolvido por extenso: um número sem régua não
               permite julgamento, e o fuso muda onde o dia começa. -->
          <span
            v-if="!unusableRange"
            class="text-label-small tabular-nums text-n-slate-10"
          >
            {{ periodLabel }}
          </span>
          <!-- Data final antes da inicial mediria zero e pareceria "não vendeu
               nada". Dizer o que está errado é o oposto de mostrar zero. -->
          <span
            v-else-if="invertedRange"
            class="text-label-small text-n-ruby-11"
            role="alert"
          >
            {{ t('PIPELINE.PRODUCTS.PERIOD_INVERTED') }}
          </span>
          <span v-else class="text-label-small text-n-slate-10">
            {{ t('PIPELINE.PRODUCTS.PERIOD_PICK_DATES') }}
          </span>
        </div>

        <div
          class="mb-3 flex flex-wrap gap-6 rounded-lg border border-n-weak px-4 py-3"
          :aria-busy="measurementPending"
        >
          <div>
            <p class="text-label-small uppercase tracking-wide text-n-slate-10">
              {{ t('PIPELINE.PRODUCTS.SUMMARY_REVENUE') }}
            </p>
            <!-- Enquanto mede, mostra "—" e NÃO zero: zero é uma afirmação
                 ("não vendeu nada"), e ainda não sabemos disso. -->
            <p
              v-if="!measurementKnown"
              class="text-heading-2 font-bold tabular-nums text-n-slate-10"
            >
              —
            </p>
            <template v-else>
              <p
                v-for="linha in totals.porMoeda"
                :key="linha.currency"
                class="text-heading-2 font-bold tabular-nums text-n-teal-11"
              >
                {{ formatMoney(linha.value, linha.currency) }}
              </p>
              <p
                v-if="!totals.porMoeda.length"
                class="text-heading-2 font-bold tabular-nums text-n-slate-10"
              >
                {{ formatMoney(0, accountCurrency) }}
              </p>
            </template>
          </div>
          <div>
            <p class="text-label-small uppercase tracking-wide text-n-slate-10">
              {{ t('PIPELINE.PRODUCTS.SUMMARY_SALES') }}
            </p>
            <p class="text-heading-2 font-bold tabular-nums text-n-slate-12">
              {{ measurementKnown ? totals.vendas : '—' }}
            </p>
          </div>
          <div>
            <p class="text-label-small uppercase tracking-wide text-n-slate-10">
              {{ t('PIPELINE.PRODUCTS.SUMMARY_SOLD') }}
            </p>
            <p class="text-heading-2 font-bold tabular-nums text-n-slate-12">
              {{
                !measurementKnown
                  ? '—'
                  : t('PIPELINE.PRODUCTS.COUNT_FILTERED', {
                      shown: totals.produtosComVenda,
                      total: filteredProducts.length,
                    })
              }}
            </p>
            <p v-if="measurementKnown" class="text-label-small text-n-slate-11">
              {{
                t('PIPELINE.PRODUCTS.SUMMARY_NO_SALE', {
                  count: filteredProducts.length - totals.produtosComVenda,
                })
              }}
            </p>
          </div>
        </div>

        <!-- Conta a verdade em vez de exibir zeros inexplicáveis: hoje TODAS as
             vendas migradas pelo backfill caem aqui, sem produto vinculado. -->
        <div
          v-for="linha in unlinkedSales"
          :key="`unlinked-${linha.currency}`"
          class="mb-3 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
        >
          {{
            t('PIPELINE.PRODUCTS.UNLINKED', {
              value: formatMoney(linha.total_value, linha.currency),
              count: linha.sales_count,
            })
          }}
        </div>

        <p
          v-if="performanceFailed"
          class="mb-3 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ t('PIPELINE.PRODUCTS.PERF_ERROR') }}
        </p>

        <!-- Catálogo vazio troca só a LISTA, não a régua acima dela: o convite
             a cadastrar convive com a receita que a conta já tem. -->
        <div
          v-if="isEmpty"
          class="mx-auto max-w-md rounded-xl border border-dashed border-n-weak p-8 text-center"
        >
          <p class="text-heading-2 font-semibold text-n-slate-12">
            {{ t('PIPELINE.PRODUCTS.EMPTY_TITLE') }}
          </p>
          <p class="mt-2 text-body-main text-n-slate-11">
            {{ t('PIPELINE.PRODUCTS.EMPTY_BODY') }}
          </p>
          <Button
            v-if="canManage"
            class="mt-4"
            color="blue"
            size="md"
            :label="t('PIPELINE.PRODUCTS.NEW')"
            @click="openNew"
          />
        </div>

        <div v-else class="flex flex-col gap-5 lg:flex-row lg:items-start">
          <!-- Facetas: contagens sobre o catálogo inteiro, para o usuário
             descobrir o que existe antes de clicar. -->
          <aside
            class="flex flex-shrink-0 flex-col gap-5 lg:w-52 lg:border-r lg:border-n-weak lg:pr-5"
          >
            <div class="relative">
              <span
                class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
              >
                <Icon icon="i-lucide-search" class="size-4" />
              </span>
              <!-- `!pl-9` e `!mb-0` com bang: o CSS global de formulário
                 (_base.scss) sobrescreve padding e ainda dá margin-bottom ao
                 input. Sem o bang o ícone fica POR CIMA do texto e 8px fora do
                 centro, porque a altura do pai passa a incluir a margem. -->
              <input
                v-model="search"
                type="search"
                name="product-search"
                class="h-9 w-full rounded-lg border border-n-weak bg-n-solid-2 !mb-0 !pl-9 pr-3 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand"
                :placeholder="t('PIPELINE.PRODUCTS.SEARCH_PLACEHOLDER')"
                :aria-label="t('PIPELINE.PRODUCTS.SEARCH_PLACEHOLDER')"
              />
            </div>

            <div>
              <h2
                class="mb-2 text-label-small uppercase tracking-wide text-n-slate-10"
              >
                {{ t('PIPELINE.PRODUCTS.FACET_STATUS') }}
              </h2>
              <button
                v-for="option in [
                  { key: 'all', label: t('PIPELINE.PRODUCTS.FACET_ALL') },
                  { key: 'active', label: t('PIPELINE.PRODUCTS.ACTIVE') },
                  { key: 'inactive', label: t('PIPELINE.PRODUCTS.INACTIVE') },
                ]"
                :key="option.key"
                type="button"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-n-alpha-1"
                :class="
                  statusFilter === option.key
                    ? 'font-semibold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                :aria-pressed="statusFilter === option.key"
                @click="statusFilter = option.key"
              >
                <span class="flex-1 truncate">{{ option.label }}</span>
                <span class="text-label-small tabular-nums text-n-slate-10">
                  {{ statusCounts[option.key] }}
                </span>
              </button>
            </div>

            <div
              v-if="pipelineFacets.named.length || pipelineFacets.unrestricted"
            >
              <h2
                class="mb-2 text-label-small uppercase tracking-wide text-n-slate-10"
              >
                {{ t('PIPELINE.PRODUCTS.FACET_PIPELINE') }}
              </h2>
              <button
                v-if="pipelineFacets.unrestricted"
                type="button"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-n-alpha-1"
                :class="
                  pipelineFilter === 'unrestricted'
                    ? 'font-semibold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                :aria-pressed="pipelineFilter === 'unrestricted'"
                @click="
                  pipelineFilter =
                    pipelineFilter === 'unrestricted' ? null : 'unrestricted'
                "
              >
                <span class="flex-1 truncate">
                  {{ t('PIPELINE.PRODUCTS.ALL_PIPELINES') }}
                </span>
                <span class="text-label-small tabular-nums text-n-slate-10">
                  {{ pipelineFacets.unrestricted }}
                </span>
              </button>
              <button
                v-for="facet in pipelineFacets.named"
                :key="facet.id"
                type="button"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-n-alpha-1"
                :class="
                  pipelineFilter === facet.id
                    ? 'font-semibold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                :aria-pressed="pipelineFilter === facet.id"
                @click="
                  pipelineFilter = pipelineFilter === facet.id ? null : facet.id
                "
              >
                <span class="flex-1 truncate">{{ facet.name }}</span>
                <span class="text-label-small tabular-nums text-n-slate-10">
                  {{ facet.count }}
                </span>
              </button>
            </div>

            <div v-if="categoryFacets.length">
              <h2
                class="mb-2 text-label-small uppercase tracking-wide text-n-slate-10"
              >
                {{ t('PIPELINE.PRODUCTS.FACET_CATEGORY') }}
              </h2>
              <button
                v-for="facet in categoryFacets"
                :key="facet.name"
                type="button"
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-n-alpha-1"
                :class="
                  categoryFilter === facet.name
                    ? 'font-semibold text-n-slate-12'
                    : 'text-n-slate-11'
                "
                :aria-pressed="categoryFilter === facet.name"
                @click="
                  categoryFilter =
                    categoryFilter === facet.name ? null : facet.name
                "
              >
                <span class="flex-1 truncate">{{ facet.name }}</span>
                <span class="text-label-small tabular-nums text-n-slate-10">
                  {{ facet.count }}
                </span>
              </button>
            </div>

            <Button
              v-if="hasActiveFilters"
              variant="outline"
              color="slate"
              size="sm"
              :label="t('PIPELINE.PRODUCTS.CLEAR_FILTERS')"
              @click="clearFilters"
            />
          </aside>

          <div class="min-w-0 flex-1">
            <div class="mb-3 flex flex-wrap items-center gap-3">
              <!-- Contagem RELATIVA: só "3" faria parecer que o catálogo
                 encolheu; "3 de 14" diz que o resto está filtrado. -->
              <span class="text-label-small tabular-nums text-n-slate-11">
                {{
                  hasActiveFilters
                    ? t('PIPELINE.PRODUCTS.COUNT_FILTERED', {
                        shown: filteredProducts.length,
                        total: products.length,
                      })
                    : t('PIPELINE.PRODUCTS.COUNT_TOTAL', products.length)
                }}
              </span>
              <label class="ml-auto flex items-center gap-2">
                <span
                  class="whitespace-nowrap text-label-small text-n-slate-10"
                >
                  {{ t('PIPELINE.PRODUCTS.SORT_LABEL') }}
                </span>
                <select
                  v-model="sortBy"
                  name="product-sort"
                  class="h-8 rounded-lg border border-n-weak bg-n-solid-2 bg-none !mb-0 px-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                >
                  <option value="name">
                    {{ t('PIPELINE.PRODUCTS.SORT_NAME') }}
                  </option>
                  <option value="value_desc">
                    {{ t('PIPELINE.PRODUCTS.SORT_VALUE') }}
                  </option>
                  <option value="recent">
                    {{ t('PIPELINE.PRODUCTS.SORT_RECENT') }}
                  </option>
                </select>
              </label>
            </div>

            <!-- Busca sem resultado é OUTRO estado: aqui a saída é afrouxar o
               filtro, não cadastrar um produto novo. -->
            <div
              v-if="hasNoResults"
              class="rounded-xl border border-dashed border-n-weak p-8 text-center"
            >
              <p class="text-body-main font-medium text-n-slate-12">
                {{ t('PIPELINE.PRODUCTS.NO_RESULTS_TITLE') }}
              </p>
              <p class="mt-1 text-body-main text-n-slate-11">
                {{ t('PIPELINE.PRODUCTS.NO_RESULTS_BODY') }}
              </p>
              <Button
                class="mt-4"
                variant="outline"
                color="slate"
                size="sm"
                :label="t('PIPELINE.PRODUCTS.CLEAR_FILTERS')"
                @click="clearFilters"
              />
            </div>

            <ul v-else class="flex flex-col gap-2">
              <li
                v-for="product in filteredProducts"
                :key="product.id"
                class="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-n-weak p-3.5"
                :class="product.active ? 'bg-n-solid-2' : 'bg-n-solid-1'"
              >
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span
                      class="font-medium text-n-slate-12"
                      :class="{ 'opacity-70': !product.active }"
                    >
                      {{ product.name }}
                    </span>
                    <span
                      v-if="product.sku"
                      class="font-mono text-label-small text-n-slate-11"
                    >
                      {{ product.sku }}
                    </span>
                    <span
                      class="rounded-full px-2 py-0.5 text-label-small font-medium"
                      :class="
                        product.active
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-alpha-2 text-n-slate-11'
                      "
                    >
                      {{
                        product.active
                          ? t('PIPELINE.PRODUCTS.ACTIVE')
                          : t('PIPELINE.PRODUCTS.INACTIVE')
                      }}
                    </span>
                  </div>
                  <p class="mt-0.5 truncate text-label-small text-n-slate-10">
                    <span v-if="product.category">
                      {{ product.category }} ·
                    </span>
                    <span
                      :class="{
                        'text-n-ruby-11': product.pipeline_ids_malformed,
                      }"
                    >
                      {{ pipelineNames(product) }}
                    </span>
                  </p>
                </div>

                <span
                  class="whitespace-nowrap font-semibold tabular-nums text-n-slate-12"
                >
                  {{ formatMoney(product.default_value, product.currency) }}
                </span>

                <!-- Vendas e Receita em colunas SEPARADAS: volume e valor
                   respondem perguntas diferentes. Juntos num número só, some a
                   distinção entre "vende muito barato" e "pouco e caro". -->
                <!-- Sem medição válida, a linha NÃO afirma "0 vendas": zero é
                     uma afirmação sobre o período, e medindo (ou depois de a
                     medição falhar) ela seria falsa. -->
                <div class="w-20 text-right tabular-nums">
                  <span
                    class="block font-semibold"
                    :class="
                      !measurementKnown || !salesCountOf(product)
                        ? 'text-n-slate-10'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ measurementKnown ? salesCountOf(product) : '—' }}
                  </span>
                  <span class="text-label-small text-n-slate-10">
                    {{ t('PIPELINE.PRODUCTS.COL_SALES') }}
                  </span>
                </div>

                <div class="w-36 text-right tabular-nums">
                  <template
                    v-if="measurementKnown && performanceOf(product).length"
                  >
                    <span
                      v-for="linha in performanceOf(product)"
                      :key="linha.currency"
                      class="block font-semibold text-n-teal-11"
                    >
                      {{ formatMoney(linha.total_value, linha.currency) }}
                    </span>
                  </template>
                  <span
                    v-else
                    class="block font-semibold text-n-slate-10"
                    aria-hidden="true"
                  >
                    —
                  </span>
                  <span class="text-label-small text-n-slate-10">
                    {{
                      !measurementKnown
                        ? t('PIPELINE.PRODUCTS.PERF_UNKNOWN')
                        : performanceOf(product).length
                          ? t('PIPELINE.PRODUCTS.COL_REVENUE')
                          : t('PIPELINE.PRODUCTS.PERF_NONE')
                    }}
                  </span>
                </div>

                <div v-if="canManage" class="flex gap-2">
                  <Button
                    variant="outline"
                    color="slate"
                    size="sm"
                    :label="t('PIPELINE.PRODUCTS.EDIT')"
                    @click="openEdit(product)"
                  />
                  <Button
                    v-if="product.active"
                    variant="outline"
                    color="ruby"
                    size="sm"
                    :label="t('PIPELINE.PRODUCTS.DEACTIVATE')"
                    @click="deactivate(product)"
                  />
                  <Button
                    v-else
                    variant="outline"
                    color="teal"
                    size="sm"
                    :label="t('PIPELINE.PRODUCTS.REACTIVATE')"
                    @click="reactivate(product)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </template>
    </div>

    <ProductFormModal
      v-if="showForm"
      :show="showForm"
      :product="editing"
      :pipelines="pipelines"
      @close="showForm = false"
      @saved="onSaved"
    />
  </div>
</template>
